import { IRegulatoryRepository } from "@/repositories/RegulatoryRepository";
import {
  RegulatoryApproval,
  ApprovalFilters,
  ApprovalSummary,
  ExpiryAlert,
  DocumentVersion,
  TimelineEvent,
  ActivationStatus,
  RegulatoryAuthority,
  ApprovalCategory,
} from "@/types/regulatory/regulatory.type";
import {
  assertUploadWithin10Mb,
  uploadSizeLimitApiMessage,
} from "@/constants/uploadLimits";
import { getErrorMessage } from "@/utils/errorMessage";

interface ApprovalFormPayload {
  study_id?: string;
  studyId?: string;
  approval_number?: string;
  approvalNumber?: string;
  valid_from?: string;
  validFrom?: string;
}

export interface IRegulatoryUseCase {
  // Approval operations
  getApprovals(
    studyId: string,
    filters?: ApprovalFilters,
  ): Promise<RegulatoryApproval[]>;
  getApprovalById(
    studyId: string,
    approvalId: string,
  ): Promise<RegulatoryApproval>;
  createApproval(
    studyId: string,
    approval: Omit<RegulatoryApproval, "id" | "createdBy">,
  ): Promise<RegulatoryApproval>;
  updateApproval(
    studyId: string,
    approvalId: string,
    updates: Partial<RegulatoryApproval>,
  ): Promise<RegulatoryApproval>;
  archiveApproval(studyId: string, approvalId: string): Promise<void>;

  // Document operations
  uploadDocument(
    studyId: string,
    approvalId: string,
    file: File,
    versionNotes?: string,
  ): Promise<DocumentVersion>;
  getDocumentVersions(
    studyId: string,
    approvalId: string,
  ): Promise<DocumentVersion[]>;
  downloadDocument(documentUrl: string): Promise<Blob>;
  downloadDocumentStream(
    studyId: string,
    approvalId: string,
    documentId: string,
  ): Promise<Blob>;

  // Timeline operations
  getTimeline(studyId: string, approvalId: string): Promise<TimelineEvent[]>;

  // Summary and alerts
  getApprovalSummary(studyId: string): Promise<ApprovalSummary>;
  getSummary(studyId: string): Promise<ApprovalSummary>;
  getExpiryAlerts(studyId: string, withinDays?: number): Promise<ExpiryAlert[]>;
  getExpiryAlertsBySiteId(
    studyId: string,
    siteId: string,
    withinDays?: number,
  ): Promise<ExpiryAlert[]>;

  // Documents
  getDocuments(studyId: string, approvalId: string): Promise<DocumentVersion[]>;

  // Activation workflow
  checkActivationStatus(
    studyId: string,
    siteId: string,
  ): Promise<ActivationStatus>;

  // Site-specific approvals
  getApprovalsBySiteId(
    studyId: string,
    siteId: string,
    filters?: ApprovalFilters,
  ): Promise<RegulatoryApproval[]>;

  // Regulatory authorities
  getAuthorities(
    category?: ApprovalCategory,
    country?: string,
    active?: boolean,
  ): Promise<RegulatoryAuthority[]>;
}

export class RegulatoryUseCase implements IRegulatoryUseCase {
  constructor(private repository: IRegulatoryRepository) {}

  async getApprovals(
    studyId: string,
    filters?: ApprovalFilters,
  ): Promise<RegulatoryApproval[]> {
    try {
      return await this.repository.getApprovals(studyId, filters);
    } catch (error) {
      throw new Error(
        error instanceof Error
          ? error.message
          : "Failed to fetch regulatory approvals",
      );
    }
  }

  async getApprovalById(
    studyId: string,
    approvalId: string,
  ): Promise<RegulatoryApproval> {
    try {
      return await this.repository.getApprovalById(studyId, approvalId);
    } catch (error) {
      throw new Error(
        error instanceof Error
          ? error.message
          : "Failed to fetch approval details",
      );
    }
  }

  async createApproval(
    studyId: string,
    approval: Omit<RegulatoryApproval, "id" | "createdBy">,
  ): Promise<RegulatoryApproval> {
    try {
      // The approval object comes with snake_case fields from the form
      const approvalData = approval as ApprovalFormPayload & typeof approval;

      // Validate required fields (check both camelCase and snake_case for compatibility)
      // approval_number is intentionally NOT required — it's a valid business case
      // for an approval to be submitted without a formal reference number.
      if (
        !(approvalData.study_id || approvalData.studyId) ||
        !(approvalData.valid_from || approvalData.validFrom)
      ) {
        throw new Error("Missing required fields: study_id, valid_from");
      }

      // Extract studyId from approval data if not provided
      const effectiveStudyId =
        (studyId || approvalData.study_id) ?? approvalData.studyId;

      if (!effectiveStudyId) {
        throw new Error("Study ID is required");
      }

      return await this.repository.createApproval(effectiveStudyId, approval);
    } catch (error) {
      throw new Error(
        error instanceof Error
          ? error.message
          : "Failed to create regulatory approval",
      );
    }
  }

  async updateApproval(
    studyId: string,
    approvalId: string,
    updates: Partial<RegulatoryApproval>,
  ): Promise<RegulatoryApproval> {
    try {
      return await this.repository.updateApproval(studyId, approvalId, updates);
    } catch (error) {
      throw new Error(
        error instanceof Error
          ? error.message
          : "Failed to update regulatory approval",
      );
    }
  }

  async archiveApproval(studyId: string, approvalId: string): Promise<void> {
    try {
      return await this.repository.archiveApproval(studyId, approvalId);
    } catch (error) {
      throw new Error(
        error instanceof Error
          ? error.message
          : "Failed to archive regulatory approval",
      );
    }
  }

  async uploadDocument(
    studyId: string,
    approvalId: string,
    file: File,
    versionNotes?: string,
  ): Promise<DocumentVersion> {
    try {
      // Validate file exists
      if (!file) {
        throw new Error("File is required");
      }

      // Validate it's a File object
      if (!(file instanceof File)) {
        throw new Error("Invalid file object - must be a File instance");
      }

      assertUploadWithin10Mb(file);

      const allowedTypes = [
        "application/pdf",
        "application/msword",
        "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
        "image/jpeg",
        "image/png",
      ];
      if (!allowedTypes.includes(file.type)) {
        throw new Error(
          `Invalid file type: ${file.type}. Allowed: PDF, DOCX, JPG, PNG`,
        );
      }

      const result = await this.repository.uploadDocument(
        studyId,
        approvalId,
        file,
        versionNotes,
      );

      return result;
    } catch (error: unknown) {
      const sizeMessage = uploadSizeLimitApiMessage(error);
      throw new Error(
        sizeMessage ?? getErrorMessage(error, "Failed to upload document"),
      );
    }
  }

  async getDocumentVersions(
    studyId: string,
    approvalId: string,
  ): Promise<DocumentVersion[]> {
    try {
      return await this.repository.getDocumentVersions(studyId, approvalId);
    } catch (error) {
      throw new Error(
        error instanceof Error
          ? error.message
          : "Failed to fetch document versions",
      );
    }
  }

  async downloadDocument(documentUrl: string): Promise<Blob> {
    try {
      return await this.repository.downloadDocument(documentUrl);
    } catch (error) {
      throw new Error(
        error instanceof Error ? error.message : "Failed to download document",
      );
    }
  }

  async downloadDocumentStream(
    studyId: string,
    approvalId: string,
    documentId: string,
  ): Promise<Blob> {
    try {
      return await this.repository.downloadDocumentStream(
        studyId,
        approvalId,
        documentId,
      );
    } catch (error) {
      throw new Error(
        error instanceof Error ? error.message : "Failed to download document",
      );
    }
  }

  async getTimeline(
    studyId: string,
    approvalId: string,
  ): Promise<TimelineEvent[]> {
    try {
      return await this.repository.getTimeline(studyId, approvalId);
    } catch (error) {
      throw new Error(
        error instanceof Error
          ? error.message
          : "Failed to fetch approval timeline",
      );
    }
  }

  async getApprovalSummary(studyId: string): Promise<ApprovalSummary> {
    try {
      return await this.repository.getApprovalSummary(studyId);
    } catch (error) {
      throw new Error(
        error instanceof Error
          ? error.message
          : "Failed to fetch approval summary",
      );
    }
  }

  async getExpiryAlerts(
    studyId: string,
    withinDays?: number,
  ): Promise<ExpiryAlert[]> {
    try {
      return await this.repository.getExpiryAlerts(studyId, withinDays);
    } catch (error) {
      throw new Error(
        error instanceof Error
          ? error.message
          : "Failed to fetch expiry alerts",
      );
    }
  }

  async getExpiryAlertsBySiteId(
    studyId: string,
    siteId: string,
    withinDays?: number,
  ): Promise<ExpiryAlert[]> {
    try {
      if (!studyId) {
        throw new Error("Study ID is required");
      }
      if (!siteId) {
        throw new Error("Site ID is required");
      }
      return await this.repository.getExpiryAlertsBySiteId(
        studyId,
        siteId,
        withinDays,
      );
    } catch (error) {
      throw new Error(
        error instanceof Error
          ? error.message
          : "Failed to fetch expiry alerts by site ID",
      );
    }
  }

  // Alias for getSummary - points to getApprovalSummary
  async getSummary(studyId: string): Promise<ApprovalSummary> {
    return this.getApprovalSummary(studyId);
  }

  // Alias for getDocuments - points to getDocumentVersions
  async getDocuments(
    studyId: string,
    approvalId: string,
  ): Promise<DocumentVersion[]> {
    return this.getDocumentVersions(studyId, approvalId);
  }

  async checkActivationStatus(
    studyId: string,
    siteId: string,
  ): Promise<ActivationStatus> {
    try {
      return await this.repository.checkActivationStatus(studyId, siteId);
    } catch (error) {
      throw new Error(
        error instanceof Error
          ? error.message
          : "Failed to check activation status",
      );
    }
  }

  async getApprovalsBySiteId(
    studyId: string,
    siteId: string,
    filters?: ApprovalFilters,
  ): Promise<RegulatoryApproval[]> {
    try {
      if (!siteId) {
        throw new Error("Site ID is required");
      }

      return await this.repository.getApprovalsBySiteId(
        studyId,
        siteId,
        filters,
      );
    } catch (error) {
      throw new Error(
        error instanceof Error
          ? error.message
          : "Failed to fetch approvals by site ID",
      );
    }
  }

  async getAuthorities(
    category?: ApprovalCategory,
    country?: string,
    active?: boolean,
  ): Promise<RegulatoryAuthority[]> {
    try {
      return await this.repository.getAuthorities(category, country, active);
    } catch (error) {
      throw new Error(
        error instanceof Error
          ? error.message
          : "Failed to fetch regulatory authorities",
      );
    }
  }
}
